import React, { useCallback, useState, useEffect, useContext } from 'react';
import { Tabs, Tab, Container } from 'react-bootstrap';

import { ApiServiceServerless } from '../../xhr_libs';
import UserManagement from '../../components/UserManagement';
import OrganizationUtilityImportSettingsTabs from './OrganizationUtilityImportSettingsTabs';
import OrganizationPortfolioManagement from './OrganizationPortfolioManagement';
import PageHeader from '../../components/PageHeader';
import { useTabUrl } from '../../helpers/tracked';

import Helmet from 'react-helmet';
import {
  useOrganizationRoles,
  useOrganizationRolesWeight,
} from '../../helpers/checkRoles';
import OrganizationContacts from './OrganizationContacts';
import OrganizationDetails from './OrganizationDetails';

import AppContext from '../../contexts/app-context';

const pageTitle = 'Organization: ';

const DEFAULT_ORG_VALUES = {
  id: '',
  name: '',
  address: {},
  contacts: {},
  organization_type: '',
  buildings: [],
  portfolios: [],
};

function EditOrganization(props) {
  const { match, setActiveOrganization, showToast } = props;
  const { isAdmin } = useContext(AppContext);

  const [organization, setOrganization] = useState({ ...DEFAULT_ORG_VALUES });
  const [organizationDisplayName, setOrganizationDisplayName] = useState('');
  const [isLoading, setIsLoading] = useState(true);

  const [activeTab, setActiveTab] = useTabUrl('organization-details');
  const [activeTabList, setActiveTabList] = useState(['organization-details']);

  const addNewActiveTab = useCallback(
    (tab) => {
      if (tab !== activeTab) {
        setActiveTab(tab);
      }
      if (!activeTabList.includes(tab)) {
        let temp_keys = [...activeTabList];
        temp_keys.push(tab);
        setActiveTabList(temp_keys);
      }
    },
    [activeTab, activeTabList, setActiveTab]
  );

  useEffect(() => {
    addNewActiveTab(activeTab);
  }, [activeTab, addNewActiveTab]);

  const organizationUserCreateAccess = useOrganizationRoles(
    organization,
    'organization_user',
    'create'
  );
  const organizationUserUpdateAccess = useOrganizationRoles(
    organization,
    'organization_user',
    'update'
  );
  const organizationUserDeleteAccess = useOrganizationRoles(
    organization,
    'organization_user',
    'delete'
  );

  const deleteAccess = useOrganizationRoles(
    organization,
    'organization',
    'delete'
  );
  const updateAccess = useOrganizationRoles(
    organization,
    'organization',
    'update'
  );

  const buildingUpdateAccess = useOrganizationRoles(
    organization,
    'building',
    'update'
  );

  const buildingCreateAccess = useOrganizationRoles(
    organization,
    'building',
    'create'
  );

  const accountCreateAccess = useOrganizationRoles(
    organization,
    'utility_account',
    'create'
  );

  const organizationRoleWeight = useOrganizationRolesWeight(organization);

  const getOrganization = useCallback(() => {
    const id = parseInt(match.params.id);
    if (id) {
      ApiServiceServerless.get(`/organizations/${id}`, { authorization_id: id })
        .then((res) => {
          let org = { ...res.data };
          setOrganization({ ...org });
          setActiveOrganization(res.data);
        })
        .catch((error) => {
          showToast('danger', 'Error', error);
          throw error;
        })
        .finally(() => setIsLoading(false));
    }
  }, [match.params.id, showToast, setActiveOrganization]);

  useEffect(() => {
    getOrganization();
  }, [getOrganization]);

  useEffect(() => {
    setOrganizationDisplayName(organization.name);
  }, [organization.name, setOrganizationDisplayName]);

  return (
    <Container className='et-container et-main-tab-content'>
      <Helmet>
        <title>
          {pageTitle}
          {organizationDisplayName}
        </title>
      </Helmet>
      <PageHeader
        titlePrepend={pageTitle}
        title={
          organizationDisplayName
            ? organizationDisplayName
            : 'Manage Organizations'
        }
      />
      <Tabs
        id='organization-tabs'
        activeKey={activeTab}
        onSelect={(e) => addNewActiveTab(e)}
      >
        <Tab eventKey='organization-details' title='Details'>
          {activeTabList.includes('organization-details') && (
            <OrganizationDetails
              org_id={parseInt(match.params.id)}
              setOrganizationDisplayName={setOrganizationDisplayName}
              organization={organization}
              showToast={showToast}
              updateAccess={updateAccess}
              deleteAccess={deleteAccess}
              buildingCreateAccess={buildingCreateAccess}
              buildingUpdateAccess={buildingUpdateAccess}
              accountCreateAccess={accountCreateAccess}
              setOrganization={setOrganization}
              isLoading={isLoading}
              isAdmin={isAdmin}
            />
          )}
        </Tab>

        <Tab eventKey='user-management' title='Users'>
          {activeTabList.includes('user-management') && (
            <UserManagement
              entityType={'organization'}
              entityId={parseInt(match.params.id)}
              showToast={showToast}
              createAccess={organizationUserCreateAccess}
              updateAccess={organizationUserUpdateAccess}
              deleteAccess={organizationUserDeleteAccess}
              roleWeight={organizationRoleWeight}
            />
          )}
        </Tab>
        <Tab eventKey='contact-management' title='Contacts'>
          {activeTabList.includes('contact-management') && (
            <OrganizationContacts
              organization={organization}
              contacts={organization.contacts}
              isLoading={isLoading}
              showToast={showToast}
              setOrganization={setOrganization}
            />
          )}
        </Tab>
        <Tab eventKey='portfolio-management' title='Portfolios'>
          {activeTabList.includes('portfolio-management') && (
            <OrganizationPortfolioManagement
              organization={organization}
              showToast={showToast}
            />
          )}
        </Tab>

        <Tab eventKey='utility-import-settings' title='Data Providers'>
          {activeTabList.includes('utility-import-settings') && (
            <OrganizationUtilityImportSettingsTabs
              organization={organization}
              organizationId={parseInt(match.params.id)}
              showToast={showToast}
            />
          )}
        </Tab>
      </Tabs>
    </Container>
  );
}

export default EditOrganization;
